import React from 'react';
import { RcsComponent, RcsButton } from './rcs-types';
import { CheckCircle2, Phone, ExternalLink, MapPin, Calendar } from 'lucide-react';

interface RcsPreviewProps {
  components: RcsComponent[];
  senderName?: string;
}

// Helper to get text content (API uses 'text', types use 'content')
const getTextContent = (comp: any): string | null => {
  return comp?.content || comp?.text || null;
};

// Helper to get media URL (API uses 'media_url', types use 'mediaUrl')
const getMediaUrl = (comp: any): string | null => {
  return comp?.mediaUrl || comp?.media_url || null;
};

// Helper to normalize actions/buttons (API uses 'actions', types use 'buttons')
const getButtons = (comp: any): any[] => {
  return comp?.buttons || comp?.actions || [];
};

// Helper to get replies from suggested_replies (API uses objects with text, types use strings)
const getReplies = (comp: any): string[] => {
  const replies = comp?.replies || [];
  if (replies.length === 0) return [];
  // Handle both string[] and { text: string, postback_data?: string }[]
  return replies.map((r: any) => typeof r === 'string' ? r : r?.text || '').filter(Boolean);
};

// Helper to get button action type
const getButtonActionType = (btn: any): string => {
  // Types format: action = 'dial' | 'open_url' | 'location' | 'calendar'
  // API format: type = 'PHONE_NUMBER' | 'URL' | 'LOCATION' | 'CALENDAR'
  if (btn.action) return btn.action;
  const typeMap: Record<string, string> = {
    'PHONE_NUMBER': 'dial',
    'URL': 'open_url',
    'LOCATION': 'location',
    'CALENDAR': 'calendar',
  };
  return typeMap[btn.type] || 'open_url';
};

export const RcsPreview: React.FC<RcsPreviewProps> = ({
  components = [],
  senderName = 'Darwin Stores',
}) => {
  const renderComponent = (component: RcsComponent, index: number) => {
    const c = component as any;

    switch (component.type) {
      case 'text':
        return (
          <div key={component.id || index} className="bg-white rounded-2xl px-3 py-2 shadow-sm max-w-[85%]">
            <p className="text-xs text-gray-800 whitespace-pre-wrap">
              {getTextContent(c) || 'Your message text here...'}
            </p>
            <p className="text-[9px] text-[#7B7B7B] font-semibold text-right mt-1">9:41</p>
          </div>
        );

      case 'image':
      case 'video':
        const mediaUrl = getMediaUrl(c);
        return (
          <div key={component.id || index} className="bg-white rounded-2xl overflow-hidden shadow-sm max-w-[85%]">
            {mediaUrl ? (
              component.type === 'image' ? (
                <img
                  src={mediaUrl}
                  alt="Preview"
                  className="w-full h-28 object-cover"
                />
              ) : (
                <video
                  src={mediaUrl}
                  className="w-full h-28 object-cover"
                />
              )
            ) : (
              <div className="w-full h-28 bg-gray-200 flex items-center justify-center">
                <span className="text-xs text-[#7B7B7B] font-semibold">
                  {component.type === 'image' ? 'Image' : 'Video'}
                </span>
              </div>
            )}
            <p className="text-[9px] text-[#7B7B7B] font-semibold text-right px-3 py-1">9:41</p>
          </div>
        );

      case 'rich_card':
        const isHorizontal = c.orientation === 'HORIZONTAL';
        const richCardMediaUrl = getMediaUrl(c);
        const richCardButtons = getButtons(c);
        const heightKey = (c.mediaHeight || c.media_height || 'MEDIUM') as 'SHORT' | 'MEDIUM' | 'TALL';
        const mediaHeightClass = {
          SHORT: 'h-16',
          MEDIUM: 'h-24',
          TALL: 'h-32',
        }[heightKey];

        if (isHorizontal) {
          return (
            <div key={component.id || index} className="bg-white rounded-2xl overflow-hidden shadow-sm max-w-[85%] flex">
              {richCardMediaUrl ? (
                <img
                  src={richCardMediaUrl}
                  alt="Card"
                  className="w-20 h-auto object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-20 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-[8px] text-[#7B7B7B] font-semibold">Image</span>
                </div>
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="px-2 py-2 flex-1">
                  <p className="text-[10px] font-semibold text-gray-800 truncate">
                    {c.title || 'Card Title'}
                  </p>
                  <p className="text-[9px] text-gray-500 mt-0.5 line-clamp-2">
                    {c.description || 'Card description...'}
                  </p>
                </div>
                {richCardButtons.length > 0 && (
                  <div className="border-t border-gray-100 px-1.5 py-1 flex gap-1">
                    {richCardButtons.slice(0, 2).map((btn: any, btnIdx: number) => {
                      const actionType = getButtonActionType(btn);
                      return (
                        <button
                          key={btn.id || btnIdx}
                          className="flex-1 flex items-center justify-center gap-0.5 px-1 py-1 bg-blue-50 rounded text-[8px] font-medium text-blue-600"
                        >
                          {actionType === 'dial' && <Phone size={8} />}
                          {actionType === 'open_url' && <ExternalLink size={8} />}
                          {btn.text || 'Button'}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div key={component.id || index} className="bg-white rounded-2xl overflow-hidden shadow-sm max-w-[85%]">
            {richCardMediaUrl ? (
              <img
                src={richCardMediaUrl}
                alt="Card"
                className={`w-full ${mediaHeightClass} object-cover`}
              />
            ) : (
              <div className={`w-full ${mediaHeightClass} bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
                <span className="text-xs text-[#7B7B7B] font-semibold">Card Image</span>
              </div>
            )}
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-gray-800">
                {c.title || 'Card Title'}
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {c.description || 'Card description goes here...'}
              </p>
            </div>
            {richCardButtons.length > 0 && (
              <div className="border-t border-gray-100 px-2 py-1.5 flex gap-1">
                {richCardButtons.slice(0, 2).map((btn: any, btnIdx: number) => {
                  const actionType = getButtonActionType(btn);
                  return (
                    <button
                      key={btn.id || btnIdx}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-50 rounded-lg text-[10px] font-medium text-blue-600"
                    >
                      {actionType === 'dial' && <Phone size={10} />}
                      {actionType === 'open_url' && <ExternalLink size={10} />}
                      {btn.text || 'Button'}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'suggested_replies':
        const replies = getReplies(c);
        return (
          <div key={component.id || index} className="flex flex-wrap gap-1.5 max-w-full">
            {replies.map((reply, idx) => (
              <button
                key={idx}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-[10px] font-medium text-gray-700 shadow-sm"
              >
                {reply}
              </button>
            ))}
          </div>
        );

      case 'cta_buttons':
      case 'call_to_action':
        const ctaButtons = getButtons(c);
        return (
          <div key={component.id || index} className="flex gap-1.5 max-w-[85%]">
            {ctaButtons.map((btn: any, btnIdx: number) => {
              const actionType = getButtonActionType(btn);
              return (
                <button
                  key={btn.id || btnIdx}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 rounded-xl text-[10px] font-medium text-white shadow-sm"
                >
                  {actionType === 'dial' && <Phone size={10} />}
                  {actionType === 'open_url' && <ExternalLink size={10} />}
                  {actionType === 'location' && <MapPin size={10} />}
                  {actionType === 'calendar' && <Calendar size={10} />}
                  {btn.text || 'Action'}
                </button>
              );
            })}
          </div>
        );

      case 'location':
        const locationLabel = c.locationLabel || c.label || 'Location';
        const locationText = c.locationText || c.text || '';
        return (
          <div key={component.id || index} className="bg-white rounded-2xl overflow-hidden shadow-sm max-w-[85%]">
            <div className="w-full h-20 bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center">
              <MapPin size={24} className="text-[#7B7B7B] font-semibold" />
            </div>
            <div className="px-3 py-2">
              <p className="text-xs font-medium text-gray-800">
                {locationLabel}
              </p>
              {locationText && (
                <p className="text-[10px] text-[#7B7B7B] font-semibold">
                  {locationText}
                </p>
              )}
              {!locationText && c.latitude && c.longitude && (
                <p className="text-[10px] text-[#7B7B7B] font-semibold">
                  {c.latitude}, {c.longitude}
                </p>
              )}
            </div>
          </div>
        );

      case 'carousel':
        const carouselCards = c.cards || [];
        const cardWidthClass = (c.cardWidth || c.card_width) === 'SMALL' ? 'w-32' : 'w-40';
        return (
          <div key={component.id || index} className="w-full overflow-x-auto -mx-3 px-3">
            <div className="flex gap-2 pb-1">
              {carouselCards.length === 0 ? (
                <div className="w-40 h-28 bg-gray-100 rounded-xl flex items-center justify-center">
                  <span className="text-[10px] text-[#7B7B7B] font-semibold">Add cards</span>
                </div>
              ) : (
                carouselCards.map((card: any, cardIdx: number) => {
                  const cardHeightKey = (card.mediaHeight || card.media_height || 'MEDIUM') as 'SHORT' | 'MEDIUM' | 'TALL';
                  const cardMediaHeightClass = {
                    SHORT: 'h-12',
                    MEDIUM: 'h-16',
                    TALL: 'h-20',
                  }[cardHeightKey];
                  const cardMediaUrl = getMediaUrl(card);
                  const cardButtons = getButtons(card);
                  return (
                    <div
                      key={card.id || cardIdx}
                      className={`${cardWidthClass} flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-sm`}
                    >
                      {cardMediaUrl ? (
                        <img
                          src={cardMediaUrl}
                          alt={card.title}
                          className={`w-full ${cardMediaHeightClass} object-cover`}
                        />
                      ) : (
                        <div className={`w-full ${cardMediaHeightClass} bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
                          <span className="text-[8px] text-[#7B7B7B] font-semibold">Image</span>
                        </div>
                      )}
                      <div className="px-2 py-1.5">
                        <p className="text-[10px] font-semibold text-gray-800 truncate">
                          {card.title || 'Card Title'}
                        </p>
                        <p className="text-[8px] text-gray-500 truncate">
                          {card.description || 'Description...'}
                        </p>
                      </div>
                      {cardButtons.length > 0 && (
                        <div className="border-t border-gray-100 px-1.5 py-1">
                          {cardButtons.slice(0, 1).map((btn: any, btnIdx: number) => {
                            const actionType = getButtonActionType(btn);
                            return (
                              <button
                                key={btn.id || btnIdx}
                                className="w-full flex items-center justify-center gap-0.5 px-1 py-1 bg-blue-50 rounded text-[8px] font-medium text-blue-600"
                              >
                                {actionType === 'dial' && <Phone size={8} />}
                                {actionType === 'open_url' && <ExternalLink size={8} />}
                                {btn.text || 'Button'}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );

      case 'divider':
        return (
          <div key={component.id || index} className="w-full flex items-center justify-center py-1">
            <div className="w-3/4 border-t border-gray-200" />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mx-auto w-full max-w-[280px]">
      <div className="bg-gray-100 rounded-[32px] overflow-hidden border-[6px] border-gray-800 shadow-xl">
        <div className="bg-gray-800 px-4 py-1 flex items-center justify-between">
          <span className="text-[10px] text-white font-medium">9:41</span>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-end gap-[1px]">
              <div className="w-[2px] h-1 bg-white rounded-sm" />
              <div className="w-[2px] h-1.5 bg-white rounded-sm" />
              <div className="w-[2px] h-2 bg-white rounded-sm" />
              <div className="w-[2px] h-2.5 bg-white rounded-sm" />
            </div>
            <div className="w-4 h-2 border border-white rounded-sm relative">
              <div className="absolute inset-0.5 bg-white rounded-[1px]" style={{ width: '70%' }} />
            </div>
          </div>
        </div>

        <div className="bg-white px-3 py-2 border-b border-gray-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
            {senderName.charAt(0)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1">
              <span className="text-sm font-semibold text-gray-800">{senderName}</span>
              <CheckCircle2 size={12} className="text-blue-500 fill-blue-500" />
            </div>
            <span className="text-[10px] text-[#7B7B7B] font-semibold">Verified business</span>
          </div>
        </div>

        <div className="min-h-[340px] max-h-[340px] overflow-y-auto bg-gray-50 p-3 space-y-2">
          {components.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-xs text-[#7B7B7B] font-semibold">Preview will appear here</p>
            </div>
          ) : (
            components.map((component, index) => renderComponent(component, index))
          )}
        </div>

        <div className="bg-white px-3 py-2 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full px-3 py-1.5">
              <span className="text-xs text-[#7B7B7B] font-semibold">Type message</span>
            </div>
            <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 h-1" />
      </div>

      <p className="text-center text-[10px] text-[#7B7B7B] font-semibold mt-2">RCS message</p>
    </div>
  );
};
